/**
 * harness/clipboard.js
 * T-Cron-2026-10-24 02:00 早间 (round-1-of-day) — 改进清单 #8「批量操作」
 *
 * 为什么抽成共享模块:
 *   PostDetail 里已经有一套「三段式复制」(主路径 → 老 webview 兜底 → 失败
 *   提示),本轮新增的「批量复制」需要**同一套语义**。两份实现必然漂移:
 *   一份修好了华为 webview 的兜底,另一份没修 ⇒ 用户看到的行为不一致。
 *   ⇒ 抽成单一事实来源,PostDetail 与 App 都调它。
 *
 * 三段式(与 PostDetail 原实现逐字等价,只是搬家):
 *   1. 主路径 navigator.clipboard.writeText —— 现代浏览器 / PWA
 *   2. 兜底 textarea + document.execCommand('copy') —— 老 webview 唯一可行的路
 *      (华为/鸿蒙 webview、微信内置浏览器、飞书内置浏览器)
 *   3. 两条都失败 ⇒ 返回 false,由调用方**必须**给用户明确反馈,
 *      不能静默返回(静默失败 = 用户以为按钮坏了)。
 *
 * 注意:本文件是 .js 且不含 JSX ⇒ vite.config.js 的 esbuild.jsxInject
 * 只对 .jsx 生效,这里不涉及 React,无需 import React。
 */

/**
 * 把文本写入剪贴板。
 * @param {string} text
 * @returns {Promise<boolean>} true = 已写入;false = 两条路径都失败
 */
export async function copyToClipboard(text) {
  if (text === null || text === undefined) return false;
  const payload = String(text);
  if (!payload) return false;

  // 1) 现代路径
  try {
    if (typeof navigator !== 'undefined'
      && navigator.clipboard
      && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(payload);
      return true;
    }
  } catch (_) {
    // 落到 2)
  }

  // 2) 老 webview 兜底:隐藏 textarea + execCommand
  //
  // 🔴🔴 T149 (2026-11-05 08:00):textarea 必须在 **finally** 里移除。
  //
  // 【原实现的错在哪】
  //   try {
  //     ...
  //     document.body.appendChild(ta);
  //     ta.select();
  //     const ok = document.execCommand('copy');
  //     document.body.removeChild(ta);   // ← 只在「不抛异常」时执行
  //     return !!ok;
  //   } catch (_) {
  //     return false;                    // ← 抛异常 ⇒ removeChild 被跳过
  //   }
  //
  //   `document.execCommand` 一旦**抛异常**(而不是返回 false),
  //   栈直接从 appendChild 跳到 catch ⇒ 那个 textarea **永久留在 DOM 里**。
  //
  // 【实测(本地 Playwright,monkeypatch 让 execCommand 抛异常)】
  //   点 3 次「复制」⇒ body 里多出 **3 个** textarea,
  //   内容分别是 538 / 14 / 475 字符的**完整文案副本**,
  //   样式 `position:fixed; left:-9999px; top:0; opacity:0`。
  //   ⇒ 每失败一次,就永久留一份文案的副本在 DOM 里(永不回收)。
  //
  // 【为什么不能只靠 removeChild 的位置】
  //   `execCommand` 抛异常在真实环境是**可达**的:
  //   跨域 iframe、某些 CSP 配置、document 未激活(未 focused)等情况。
  //   而且这是**兜底路径** —— 走到这里说明主路径已经失败了,
  //   恰恰是用户设备最破的时候,不该再往 DOM 里留垃圾。
  //
  // 【修法】把 removeChild 挪进 finally ⇒ 无论正常返回还是抛异常都清理。
  //   ⚠️ 用 `ta.remove()` 而不是 `parent.removeChild(ta)`:
  //      后者在 ta 已被移出 DOM 时会抛 NotFoundError,
  //      那个异常会**盖掉** finally 之外的真实返回值。
  let ta = null;
  try {
    ta = document.createElement('textarea');
    ta.value = payload;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, payload.length);
    const ok = document.execCommand('copy');
    return !!ok;
  } catch (_) {
    return false;
  } finally {
    // 🔴 T149:无条件清理 —— 抛异常的那条路以前会漏掉。
    try { if (ta && ta.parentNode) ta.parentNode.removeChild(ta); } catch (_) { /* noop */ }
  }
}

const ClipboardHelper = { copyToClipboard };

window.ClipboardHelper = ClipboardHelper;

export default ClipboardHelper;
